import React, { useState, useEffect } from 'react';
import { api, API_BASE } from '../utils/api';
import { Loader2, AlertCircle, Check, X, Printer, Filter, CalendarCheck, CalendarX, Calendar, Paperclip } from 'lucide-react';
import { formatDateThai, formatDays } from '../utils/helpers';

// --- ฟังก์ชันแปลงเลขไทย ---
const toThaiNumerals = (str) => {
  if (typeof str === 'undefined' || str === null) return '';
  const thaiDigits = ['๐', '๑', '๒', '๓', '๔', '๕', '๖', '๗', '๘', '๙'];
  return String(str).replace(/[0-9]/g, (digit) => thaiDigits[parseInt(digit)]);
};

const getStatusBadge = (status) => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'approved':
      return 'bg-green-100 text-green-800';
    case 'rejected':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getStatusText = (status) => {
  switch (status) {
    case 'pending': return 'รอพิจารณา';
    case 'approved': return 'อนุมัติแล้ว';
    case 'rejected': return 'ไม่อนุมัติ';
    default: return status;
  }
};

// --- ฟังก์ชัน Helper สำหรับไฟล์แนบ ---
const handlePrintAttachment = (attachmentDataUrl) => {
let printWindow;
  try {
    let imageUrl;
    if (attachmentDataUrl.startsWith('data:image')) {
      imageUrl = attachmentDataUrl;
    } else {
      const cleanPath = attachmentDataUrl.startsWith('/') ? attachmentDataUrl.substring(1) : attachmentDataUrl;
      imageUrl = `${API_BASE}${cleanPath}`;
    }

    const printContent = `
      <!DOCTYPE html>
      <html lang="th">
      <head>
        <meta charset="UTF-8">
        <title>เอกสารแนบใบลา</title>
        <style>
          @font-face { font-family: 'Sarabun'; src: url('${API_BASE}fonts/THSarabun.ttf') format('truetype'); }
          @page { size: A4 portrait; margin: 1.5cm; }
          body { 
            margin: 0; 
            padding: 0; 
            font-family: 'Sarabun', sans-serif; 
            word-break: keep-all;
            line-height: 1;
          }
          .container { width: 100%; text-align: center; }
          img { max-width: 100%; max-height: 95vh; height: auto; width: auto; object-fit: contain; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
        <div class="container">
          <img src="${imageUrl}" alt="เอกสารแนบ" /> </div>
      </body>
      </html>
    `;
    
    printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('ไม่สามารถเปิดหน้าต่างสำหรับพิมพ์ได้ กรุณาตรวจสอบการตั้งค่า Pop-up Blocker');
      return;
    }
    printWindow.document.write(printContent);
    printWindow.document.close();

    setTimeout(() => {
      printWindow.focus(); 
      printWindow.print();
    }, 1000); 

  } catch (err) {
    console.error("Error printing attachment:", err);
    if (printWindow) {
      printWindow.document.write(`<h3 style="font-family: Sarabun, sans-serif; color: red;">เกิดข้อผิดพลาด: ${err.message}</h3>`);
    } else {
      alert("เกิดข้อผิดพลาดในการแสดงไฟล์แนบ: " + err.message);
    }
  }
};

const LeaveApproval = ({ user }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending'); 
  const [processingId, setProcessingId] = useState(null); 
  const [downloadLoading, setDownloadLoading] = useState({}); // [✅ เพิ่ม] State สำหรับเช็คโหลดไฟล์ Word
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const fetchAllRequests = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.getLeaveRequests();
      setRequests(data);
    } catch (err) {
      console.error("Error fetching leave requests:", err);
      setError('ไม่สามารถดึงข้อมูลคำขอลาได้: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllRequests();
  }, []);

  const handleAction = async (action, requestId) => {
    if (processingId) return; 

    const actionText = action === 'approve' ? 'อนุมัติ' : 'ไม่อนุมัติ (หรือยกเลิกการอนุมัติ)';
    const isConfirmed = window.confirm(`คุณต้องการ ${actionText} รายการนี้ใช่หรือไม่?`);

    if (!isConfirmed) {
      return; 
    }

    setProcessingId(requestId);
    setError('');

    try {
      if (action === 'approve') {
        await api.approveLeaveRequest(requestId, user.employee_id);
      } else {
        await api.rejectLeaveRequest(requestId, user.employee_id);
      }
      await fetchAllRequests();
    } catch (err) {
      console.error(`Error ${action} request:`, err);
      setError(`เกิดข้อผิดพลาดในการ${action === 'approve' ? 'อนุมัติ' : 'ไม่อนุมัติ'}ใบลา: ` + err.message);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDownloadDocxServer = (request) => {
    const token = localStorage.getItem('auth_token');
    window.open(`${API_BASE}leave-request-download-docx?request_id=${request.id}&token=${token}`, '_blank');
  };

  const currentYear = new Date().getFullYear();
  const allYears = Array.from({ length: 6 }, (_, i) => currentYear - i); 

  const filteredRequests = requests.filter(req => {
    if (req.leave_type === 'ไปราชการ') {
      return false;
    }
    
    const requestYear = new Date(req.start_date).getFullYear();
    if (requestYear !== selectedYear) {
      return false;
    }
    
    if (filterStatus === 'all') return true;
    return req.status === filterStatus;
  })
  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return (
    <div className="space-y-8">
      
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <Calendar className="h-8 w-8 mr-3 text-blue-500" />
          จัดการใบลา
        </h1>
        <p className="text-gray-600 mt-2">
          ตรวจสอบ อนุมัติ หรือไม่อนุมัติคำขอการลาของบุคลากร
        </p>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 bg-gray-50 border-b">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Filter className="h-6 w-6 mr-2 text-blue-500" />
            ตัวกรองคำขอ
          </h2>
        </div>
        <div className="p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
            <div className="flex space-x-2 flex-wrap">
              <button
                onClick={() => setFilterStatus('pending')}
                className={`flex items-center px-4 py-2 rounded-md text-sm font-medium ${
                  filterStatus === 'pending'
                    ? 'bg-blue-600 text-white shadow'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Filter className="h-4 w-4 mr-2" />
                รอพิจารณา
              </button>
              <button
                onClick={() => setFilterStatus('approved')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  filterStatus === 'approved'
                    ? 'bg-green-600 text-white shadow'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                อนุมัติแล้ว
              </button>
              <button
                onClick={() => setFilterStatus('rejected')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  filterStatus === 'rejected'
                    ? 'bg-red-600 text-white shadow'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                ไม่อนุมัติ
              </button>
              <button
                onClick={() => setFilterStatus('all')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  filterStatus === 'all'
                    ? 'bg-gray-600 text-white shadow'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                ทั้งหมด
              </button>
            </div>
            
            <div className="flex-shrink-0">
              <label htmlFor="year-filter" className="text-sm font-medium text-gray-700">เลือกปี:</label>
              <select
                id="year-filter"
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="ml-2 py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                {allYears.map(year => (
                  <option key={year} value={year}>{year + 543}</option> 
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Calendar className="h-6 w-6 mr-2 text-blue-500" />
            รายการคำขอลา (ปี {selectedYear + 543})
          </h2>
        </div>
        
        <div className="p-6">
          {loading && (
            <div className="flex justify-center items-center h-64">
              <Loader2 className="h-12 w-12 text-blue-500 animate-spin" />
            </div>
          )}

          {error && (
            <div className="p-4 rounded-md bg-red-50 text-red-700 flex items-center">
              <AlertCircle className="h-5 w-5 mr-3" />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && (
            <div className="space-y-4">
              {filteredRequests.length === 0 ? (
                <p className="text-center text-gray-500 py-10">
                  {filterStatus === 'pending' 
                    ? 'ไม่มีคำขอลาที่รอพิจารณา' 
                    : 'ไม่พบข้อมูลคำขอลาสำหรับปีที่เลือก'} 
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ผู้ยื่นลา</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ประเภท</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">วันที่ลา</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">วันที่ยื่นคำขอ</th> 
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">จำนวนวัน</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">สถานะ</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">จัดการ</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredRequests.map(req => (
                        <tr key={req.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {req.first_name} {req.last_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{req.leave_type}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {formatDateThai(req.start_date)} - {formatDateThai(req.end_date)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"> 
                            {formatDateThai(req.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-center">{formatDays(req.total_days)}</td>
                          
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(req.status)}`}>
                              {getStatusText(req.status)}
                            </span>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end space-x-2">
                              
                              {req.attachment_image && (
                                <button
                                  title="ดู/พิมพ์ ใบรับรองแพทย์"
                                  onClick={() => handlePrintAttachment(req.attachment_image)}
                                  className="p-2 text-purple-600 hover:text-purple-900 hover:bg-purple-100 rounded-md disabled:opacity-50"
                                  disabled={processingId === req.id}
                                >
                                  <Paperclip className="h-5 w-5" />
                                </button>
                              )}
                              
                              <button
                                title="ดาวน์โหลดใบลาเป็นไฟล์ Word (.docx)"
                                onClick={() => handleDownloadDocxServer(req)}
                                className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-100 rounded-md disabled:opacity-50"
                                disabled={processingId === req.id || downloadLoading[req.id]}
                              >
                                {downloadLoading[req.id] ? <Loader2 className="h-5 w-5 animate-spin" /> : <Printer className="h-5 w-5" />}
                              </button>
                              
                              {(req.status === 'pending' || req.status === 'rejected') && (
                                <button
                                  title={req.status === 'rejected' ? "อนุมัติ (เปลี่ยนสถานะจาก 'ไม่อนุมัติ')" : "อนุมัติ (หลังจากผู้บริหารลงนาม)"}
                                  onClick={() => handleAction('approve', req.id)}
                                  disabled={processingId === req.id}
                                  className="p-2 text-green-600 hover:text-green-900 hover:bg-green-100 rounded-md disabled:opacity-50"
                                >
                                  {processingId === req.id ? <Loader2 className="h-5 w-5 animate-spin" /> : <CalendarCheck className="h-5 w-5" />}
                                </button>
                              )}
                              
                              {(req.status === 'pending' || req.status === 'approved') && (
                                <button
                                  title={req.status === 'approved' ? "ยกเลิกการอนุมัติ (เปลี่ยนเป็น 'ไม่อนุมัติ')" : "ไม่อนุมัติ"}
                                  onClick={() => handleAction('reject', req.id)}
                                  disabled={processingId === req.id}
                                  className="p-2 text-red-600 hover:text-red-900 hover:bg-red-100 rounded-md disabled:opacity-50"
                                >
                                  {processingId === req.id ? <Loader2 className="h-5 w-5 animate-spin" /> : <CalendarX className="h-5 w-5" />}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaveApproval;